import { AirtableRecord, FiltersConfig, StudiesFilters } from "./types";
import { formatDates } from "./utils/utils";
import {parseISO, format } from "date-fns";

const RECORDS_URL = "/data_provider/records_full.json";
const RECORD_DETAILS_URL = "/data_provider/record_details";
const FILTER_OPTIONS_URL = "/data_provider/filter_options.json";

// Making this a global constant so that we can easily change this
// in case our estimate selection strategy changes in the future
const ESTIMATES_SUBGROUP = "primary_estimates";


let explorePromise = null

const COMPARE_STRING = [
    "source_type",
    "overall_risk_of_bias",
    "population_group",
    "sex",
    "age",
    "test_type",
]

const COMPARE_ARRAY = [
    "antibody_target",
    "isotypes_reported",
]

function applyFilters(filters: FiltersConfig, records: AirtableRecord[]) {
    if(filters.unity_aligned_only) {
        records = records.filter((r) => r.is_unity_aligned)
    }
    if(filters.publish_date.length) {    
        const [startDate, endDate] = filters.publish_date
        records = records.filter(r => {
            try {
                const d = new Date(r["publication_date"])
                return startDate <= d && d <= endDate
            } catch(err) {
                return false
            }
        })
    }
    for(const c of COMPARE_STRING) {
        if(!filters[c].length) {
            continue
        }
        const values = new Set(filters[c])
        records = records.filter((r) => values.has(r[c]))
    }
    for(const c of COMPARE_ARRAY) {
        if(!filters[c].length) {
            continue
        }
        const values = new Set(filters[c])
        records = records.filter((r) => {
            for(const v of r[c]) {
                if(values.has(v)) {
                    return true
                }
            }
            return false
        })
    }
    return records
}


export default class httpClient {
    async httpGet(url: string, useAppRoute: boolean) {
        let url_full = url;
        if (useAppRoute && process.env.REACT_APP_ROUTE) {
            url_full = process.env.REACT_APP_ROUTE + url_full;
        }
        const res = await fetch(url_full);
        if (res.status !== 200) {
            const error_msg = res.json();
            console.error(error_msg);
            return;
        }
        else {
            const response_json = await res.json();
            return response_json;
        }
    }

    async httpPost(url: string, data: Record<string, any>) {
        let url_full = url;
        if (process.env.REACT_APP_ROUTE) {
            url_full = process.env.REACT_APP_ROUTE + url_full;
        }
        const res = await fetch(url_full, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            },
            // body: JSON.stringify(data) // body data type must match "Content-Type" header
        });
        if (res.status !== 200) {
            const error_msg = res.json();
            console.error(error_msg);
            return;
        }
        else {
            const response_json = await res.json();
            return response_json;
        }
    }

    async getStyles(url: string) {

        const res = await fetch(url);
        if (res.status !== 200) {
            const error_msg = res.json();
            console.error(error_msg);
            return;
        }
        else {
            const response_json = await res.json();
            return response_json;
        }
    }

    async getRecordDetails(source_id : string) {
        const response = await this.httpGet(`${RECORD_DETAILS_URL}/${source_id}.json`, true);
        if (!response) {
            return null;
        }
        const record: AirtableRecord = response as AirtableRecord;
        return record;
    }

    async getAllFilterOptions() {
        const response = await this.httpGet(FILTER_OPTIONS_URL, true);

        const options: Record<string, any> = response;
        // We know that only these 3 isotypes will ever be reported, thus we can hardcode
        options.isotypes_reported = ["IgG", "IgA", "IgM"];
        const lastUpdated = format(parseISO(response.last_updated), "yyyy/MM/dd");
        const mostRecentPubDate = format(parseISO(response.most_recent_publication_date), "yyyy/MM/dd")

        const maxDate = parseISO(response.max_publication_end_date);
        const minDate = parseISO(response.min_publication_end_date);
        delete options.min_date;
        delete options.max_date;

        return { options, lastUpdated, maxDate, minDate, mostRecentPubDate };
    }


    preparePostBody(filters: FiltersConfig){
        const reqBodyFilters = {...filters} as Record<string, any>;
        const date = filters.publish_date as Array<Date>;
        const unity_aligned_only = filters.unity_aligned_only;
        const [startDate, endDate] = formatDates(date);
        delete reqBodyFilters.publish_date;
        delete reqBodyFilters.unity_aligned_only;

        const reqBody: Record<string, any> = {
            filters: reqBodyFilters,
            sampling_start_date: startDate,
            sampling_end_date: endDate,
            unity_aligned_only,
            estimates_subgroup: ESTIMATES_SUBGROUP
        }
        return reqBody;
    }

    async _getExploreData() {
        const response = await this.httpGet(RECORDS_URL)
        if (!response || !response.records || !response.country_seroprev_summary) {
            return {
                records: [],
                estimateGradePrevalences: []
            };
        }

        const records = response.records!.map((item: Record<string, any>) => {
            // Convert response to AirtableRecord type
            const record: AirtableRecord = item as AirtableRecord;
            return record;
        });

        const estimateGradePrevalences = this.formatEstimateGradePrevalences(response);

        return { records, estimateGradePrevalences };
    }

    async getExploreData(filters: FiltersConfig,
        only_explore_columns: Boolean=false) {

        if(!explorePromise) {
            explorePromise = this._getExploreData()
        }
        const { records, estimateGradePrevalences } = await explorePromise
        return { records: applyFilters(filters, records), estimateGradePrevalences }
    }

    formatEstimateGradePrevalences(response: any) {
        const estimateGradePrevalences = response.country_seroprev_summary!.map((record: Record<string, any>) => {
            // Convert response to AlternateAggregatedRecord type         
            const estimateSummary = record.seroprevalence_estimate_summary;
            return {
                numberOfStudies: record.n_estimates,
                testsAdministered: record.n_tests_administered,
                geographicalName: record.country,          
                alpha3Code: record.country_iso3,
                localEstimate: {
                    maxEstimate: estimateSummary.Local.max_estimate,
                    minEstimate: estimateSummary.Local.min_estimate,
                    numEstimates: estimateSummary.Local.n_estimates,
                },
                nationalEstimate: {
                    maxEstimate: estimateSummary.National.max_estimate,
                    minEstimate: estimateSummary.National.min_estimate,
                    numEstimates: estimateSummary.National.n_estimates,
                },
                regionalEstimate: {
                    maxEstimate: estimateSummary.Regional.max_estimate,
                    minEstimate: estimateSummary.Regional.min_estimate,
                    numEstimates: estimateSummary.Regional.n_estimates,
                },
                sublocalEstimate: {
                    maxEstimate: estimateSummary.Sublocal.max_estimate,
                    minEstimate: estimateSummary.Sublocal.min_estimate,
                    numEstimates: estimateSummary.Sublocal.n_estimates,
                }
            }
        });
        return estimateGradePrevalences;
    }

    async getCountryPartnershipData(filters: StudiesFilters) {

        const reqBody: Record<string, any> = {
            filters: filters,
            include_subgeography_estimates: true,
            estimates_subgroup: ESTIMATES_SUBGROUP
        }

        const response = await this.httpPost(RECORDS_URL, reqBody)
        if (!response || !response.records || !response.country_seroprev_summary) {
            return {
                records: [],
                estimateGradePrevalences: []
            };
        }

        const records = response.records!.map((item: Record<string, any>) => {
            return item as AirtableRecord;
        });

        const estimateGradePrevalences = this.formatEstimateGradePrevalences(response);

        return {records, estimateGradePrevalences};
    }
}
